<template>
    <div id="ecmMap">
        <l-map
            ref="map"
            v-if="showMap"
            :zoom="config.initZoom"
            :center="config.initLocation"
            :maxBounds="config.maxBounds"
            :options="mapOptions"
            @ready="mapReady"
            @click="mapClick"
            @update:center="centerUpdate"
            @update:zoom="zoomUpdate"
            @update:bounds="boundsUpdate">

            <!-- Base map tile layer -->
            <l-tile-layer
                :url="url"
                :errorTileUrl="errorTileUrl"
                :attribution="attribution" />

            <!-- Overlay tile layers -->
            <l-wms-tile-layer
                v-for="overlayLayer in checkedWMSOverlayLayers" 
                :key="overlayLayer.name"
                :base-url="overlayLayer.url"
                :options="overlayLayer.options"
                transparent="true"
            />
            
            <l-tile-layer
                v-for="overlayLayer in checkedNormalOverlayLayers" 
                :key="overlayLayer.name"
                :url="overlayLayer.url"
                :options="overlayLayer.options"
            />

            <l-image-overlay
                v-for="overlayLayer in checkedImageOverlayLayers" 
                :key="getOverlayLayerKey(overlayLayer.name)"
                :url="getOverlayLayerUrl(overlayLayer.url)"
                :bounds="overlayLayer.bounds"
                :crossOrigin="true"
                :opacity="1"
                :options="overlayLayer.options"
            />

            <v-geosearch 
                v-show="config.showGeosearch"
                :options="geosearchOptions">
            </v-geosearch>

            <!-- Dummy marker for showing the tooltip -->
            <!-- l-marker
                v-if="tooltipItem"
                @click="popupOpen(tooltipItem.id)"
                @mouseout="tooltipItem=null"
                :lat-lng="latLng(tooltipItem.latitude, tooltipItem.longitude)"
                :name="tooltipItem.title"
                :zIndexOffset="10000">
                 <l-icon
                    :icon-url="getItemIcon(tooltipItem)"
                    :icon-size="[32,32]"
                    :icon-anchor="[16,16]"
                    :tooltip-anchor="[16,0]"
                    :popup-anchor="[0,-16]"
                >
                </l-icon>
                <l-tooltip>{{ tooltipItem.title }}</l-tooltip>
            </l-marker -->

            <!-- div v-if="tooltipItem" class="leaflet-tooltip">
                {{ tooltipItem.title }}
            </div -->

            <ToolTip 
                v-if="tooltipItem" 
                :label="tooltipItem.title" 
                :x="tooltipItem.x" 
                :y="tooltipItem.y" 
            />

            <!-- Dummy marker for showing the popup -->
            <l-marker
                v-if="popupItem"
                @click="popupClose()"
                :lat-lng="latLng(popupItem.latitude, popupItem.longitude)"
                :name="popupItem.title"
                :zIndexOffset="10000">
                 <!-- l-icon
                    :icon-url="getItemIcon(popupItem)"
                    :icon-size="[32,32]"
                    :icon-anchor="[16,16]"
                    :tooltip-anchor="[16,0]"
                    :popup-anchor="[0,-16]"
                >
                </l-icon -->
                <l-icon
                    :icon-anchor="[0, 0]"
                    :icon-size="[1, 1]"
                    class-name="popup-marker">
                    <div class="sr-only">{{ popupItem.title }}</div>
                </l-icon>
                <l-popup @ready="popupReady($event)" :options="popupOptions">
                    <ItemCard
                        :articleApiUrl="getArticleApiUrl(popupItem)"
                        :id="popupItem.id"
                        :title="popupItem.title"
                        :image="popupItem.image"
                        :description="popupItem.description"
                        :url="popupItem.url"
                        :hasText="popupItem.hasText"
                        :hasGpx="popupItem.hasGpx"
                        :categories="popupItem.categories"
                        :routeLength="popupItem.length"
                        :sourceId="popupItem.name"
                        :popupItem="popupItem"
                        target="_blank">
                    </ItemCard>
                </l-popup>
            </l-marker>

            <!-- Routes -->
            <!-- div v-if="showRoutes && ! routesDisabled">
                <div v-for="route in routes" :key="getRouteKey(route.id)">
                    <div v-for="(segment, index) in route.waypoints" :key="'segment-'+index">
                        <l-polyline
                            v-if="hasCheckedCategory(route.categories)"
                            :lat-lngs="waypointsToLatLngs(segment)"
                            :color="getStrokeColor(route)"
                            :weight="getStrokeWeight(route)">
                            <l-popup>
                                <ItemCard
                                    :articleApiUrl="$store.state.config.routesBaseUrl + '/' + route.id"
                                    :id="route.id"
                                    :title="route.title"
                                    :image="route.image"
                                    :routeLength="getRouteLength(segment)"
                                    :description="route.description"
                                    :url="route.url"
                                    :hasText="route.hasText"
                                    :hasGpx="route.hasGpx"
                                    :target="route.target"
                                    :categories="route.categories"
                                    :badges="route.badges">
                                </ItemCard>
                            </l-popup>
                        </l-polyline>
                    </div>
                </div>
            </div -->

            <l-marker v-if="userLocation" :lat-lng="userLocation">
                <l-icon
                    :icon-anchor="[0, 0]"
                    :icon-size="[1, 1]"
                    class-name="my-location">
                    <div class="sr-only">My location</div>
                </l-icon>
            </l-marker>

       </l-map>
   </div>
</template>

<script>
import 'leaflet/dist/leaflet.css';
import 'prunecluster/dist/LeafletStyleSheet.css';
import 'leaflet-geosearch/assets/css/leaflet.css';
import { eventBus } from '../main.js';
import { store } from '../store/store';
import L from 'leaflet';
import { latLng } from 'leaflet';
import { LMap, LTileLayer, LWMSTileLayer, LImageOverlay, LMarker, LIcon, LPopup } from 'vue2-leaflet';
import { PruneCluster, PruneClusterForLeaflet } from 'vue2-leaflet-prunecluster/src/utilsPruneCluster.js';
import { OpenStreetMapProvider } from 'leaflet-geosearch';
import VGeosearch from 'vue2-leaflet-geosearch';
import ItemCard from './ItemCard.vue';
import ToolTip from './ToolTip.vue';

/**
 * Set event bus listeners that need the map ref.
 * This is called after mounting on nextTick to get the map ref.
 * Listerens which don't need the map ref are set in created hook!
 */
function setListeners(self, map)
{
    eventBus.$on('zoomIn', function() {
        map.zoomIn();
    });

    eventBus.$on('zoomOut', function() {
        map.zoomOut();
    });

    eventBus.$on('setMapZoom', function(zoom) {
        map.setZoom(zoom);
    });

    eventBus.$on('setMapCenter', function(center) {
        map.setView(center);
    });

    eventBus.$on('refreshMapSize', function() {
        map.invalidateSize();
        window.dispatchEvent(new Event('resize'));
    });

    eventBus.$on('toggleMarkers', () => {
        self.$store.commit('setShowMarkers', ! self.showMarkers);
        // Load markers if showRoutes set back to true
        if (self.showMarkers) {
            eventBus.$emit('loadMarkers', map.getBounds());
        }
    });

    eventBus.$on('toggleRoutes', () => {
        self.$store.commit('setShowRoutes', ! self.showRoutes);
        // Load routes if showRoutes set back to true
        if (self.showRoutes) {
            eventBus.$emit('loadRoutes', map.getBounds());
        }
    });

    eventBus.$on('reloadMarkersAndRoutes', function() {
        eventBus.$emit('loadMarkers', map.getBounds());
        eventBus.$emit('loadRoutes', map.getBounds());
    });

    eventBus.$on('reloadAllMarkersAndRoutes', function() {
        eventBus.$emit('loadMarkers', map.getBounds());
        eventBus.$emit('loadRoutes', map.getBounds());
        self.loadExternalMarkers();
    });

    eventBus.$on('getLocation', function() {
        let locateOptions = {
            setView: false,
            enableHighAccuracy: true,
            timeout: 10000,
            watch: true
        };
        map.stopLocate();
        self.$store.commit('setUserLocation', null);
        self.$store.commit('setGettingUserLocation', true);
        eventBus.$emit('addStatus', 'Getting user location');
        map.locate(locateOptions);
    });

    map.on('locationfound', function(e) {
        self.$store.commit('setGettingUserLocation', false);
        eventBus.$emit('removeStatus', 'Getting user location');

        // Set map center to user location on the first time
        if (! self.userLocation) {
            map.panTo([e.latitude, e.longitude], {animate: true});
        }

        self.$store.commit('setUserLocation', [e.latitude, e.longitude]);
    });

    map.on('locationerror', function(e) {
        let modal = {
            title: 'Error',
            body: e.message
        };

        self.$store.commit('setModal', modal);
        self.$store.commit('setGettingUserLocation', false);
        eventBus.$emit('removeStatus', 'Getting user location');
        console.error(e.message);
    });

    /*eventBus.$on('externalFilterChange', function(checkedExternals) {
        // Trigger load for external marker sets not previously present in checkedExternals
        if (checkedExternals.length > self.checkedExternals)
        {
            let difference = checkedExternals.filter(x => !self.checkedExternals.includes(x));
            for (let i in difference) {
                eventBus.$emit('loadExternalMarkers', difference[i], map.getBounds());
            }
        }
        self.checkedExternals = checkedExternals;
    });*/

    eventBus.$on('externalFilterChange', function(name) {
        // Trigger load for external marker set
        eventBus.$emit('loadExternals', name, map.getBounds());
    });

    // This should be called if the map element dimensions change
    eventBus.$on('mapInvalidateSize', () => {
        // Refresh leaflet map after timeout
        window.setTimeout(() => {
            map.invalidateSize();
        }, 500);
    });

    /**
     * Listeners for window.emitter events
     */
    if (window.emitter) {

        // Listener for setMapMaxBounds event dispatched from Dashboard
        window.emitter.on('setMapMaxBounds', function(bounds) {
            if (! bounds) {
                self.mapOptions.minZoom = 1;
                eventBus.$emit('mapMinZoomChanged', self.mapOptions.minZoom);
                return;
            }
            self.$store.commit('setMaxBounds', bounds);
            // Set map minZoom temporarily to 1 so we can zoom to the new bounds
            self.mapOptions.minZoom = 1;
            eventBus.$emit('mapMinZoomChanged', self.mapOptions.minZoom);

            // Check if bounds is an array; it is not a Leaflet LatLngBounds object 
            // if it is not changed and comes strainght from the map config
            if (Array.isArray(bounds)) {
                // Bounds is array, convert to Leaflet LatLngBounds object
                bounds = L.latLngBounds([
                    [bounds[0][0], bounds[0][1]], 
                    [bounds[1][0], bounds[1][1]], 
                ]);
            }
            // Check if the current initLocation is out of bounds
            if (! bounds.contains([self.config.initLocation.lat, self.config.initLocation.lng])) {
                // initLocation out of bounds, set new initLocation to bounds center
                map.setView(bounds.getCenter());
            }
            // Fit new bounds into map view
            map.fitBounds(bounds);
            // Send new initZoom and initLocation back to Dashboard once the zoom ends
            map.once('zoomend', function() {
                self.mapOptions.minZoom = map.getBoundsZoom(bounds);
                map.setZoom(self.mapOptions.minZoom);
                eventBus.$emit('mapMinZoomChanged', self.mapOptions.minZoom);
                
                window.emitter.emit('setInitZoom', map.getMinZoom());
                window.emitter.emit('setInitLocation', {
                    lat: map.getCenter().lat,
                    lng: map.getCenter().lng
                });
                window.setTimeout(function() {
                    window.emitter.emit('useCurrentLocationAndZoom');
                }, 500);
            });
        });

        window.emitter.on('zoomIn', function() {
            map.zoomIn();
        });

        window.emitter.on('zoomOut', function() {
            map.zoomOut();
        });

        window.emitter.on('setZoom', function(zoom) {
            map.setZoom(zoom);
        });

        window.emitter.on('setMapCenter', function(center) {
            map.setView(center);
        });
    }
}

export default {
    name: 'Map',
    components: {
        LMap,
        LTileLayer,
        'l-wms-tile-layer': LWMSTileLayer,
        LImageOverlay,
        LMarker,
        LIcon,
        LPopup,
        'v-geosearch': VGeosearch,
        ItemCard,
        ToolTip
    },
    store: store,
    computed: {
        config() {
            return this.$store.state.config;
        },
        markers() {
            return this.$store.state.markers;
        },
        routes() {
            return this.$store.state.routes;
        },
        showMarkers() {
            return this.$store.state.showMarkers;
        },
        showRoutes() {
            return this.$store.state.showRoutes;
        },
        routesDisabled() {
            return this.$store.state.routesDisabled;
        },
        externalMarkers() {
            return this.$store.state.externalMarkers;
        },
        externalRoutes() {
            return this.$store.state.externalRoutes;
        },
        checkedCategories() {
            return this.$store.state.checkedCategories;
        },
        checkedExternals() {
            return this.$store.state.checkedExternals;
        },
        userLocation() {
            return this.$store.state.userLocation;
        },
        followUser() {
            return this.$store.state.followUser;
        },
        popupOptions() {
            return {
                closeButton: true,
                offset: L.point(0, 12)
            };
        },
        checkedWMSOverlayLayers() {
            if (store.state.config.overlayLayers) {
                return store.state.config.overlayLayers.filter(item => item.type == 'WMS' && item.checked);
            }
            return null;
        },
        checkedNormalOverlayLayers() {
            if (store.state.config.overlayLayers) {
                return store.state.config.overlayLayers.filter(item => item.type != 'WMS' && item.type != 'image' && item.checked);
            }
            return null;
        },
        checkedImageOverlayLayers() {
            if (store.state.config.overlayLayers) {
                return store.state.config.overlayLayers.filter(item => item.type == 'image' && item.checked);
            }
            return null;
        },
        geosearchOptions() {
            //console.log(this.$t('geosearchLabel'));
            return {
                provider: new OpenStreetMapProvider(),
                searchLabel: this.$t('geosearchLabel'),
                autoClose: true
            }
        },
        geosearchLabel() {
            /*if (this.config.geosearchLabel) {
                return this.config.geosearchLabel;
            }
            return 'Enter address';*/
            return this.$t('geosearchLabel');
        },
        themeColor() {
            if (store.state.config && store.state.config.themeColor) {
                return store.state.config.themeColor;
            }
            return null;
        },
        themeTextColor() {
            if (store.state.config && store.state.config.themeColor) {
                return this.$setThemeTextColor(store.state.config.themeColor);
            }
            return null;
        },
        themeHoverColor() {
            if (store.state.config && store.state.config.themeColor) {
                return this.$setThemeHoverColor(store.state.config.themeColor);
            }
            return null;
        }
    },
    watch: {
        markers() {
            if (this.map && this.pruneCluster) {
                this.pruneClusterReDraw();
            }
        },
        routes() {
            if (this.map) {
                this.drawNativePolylines();
            }
        },
        externalMarkers() {
            if (this.map && this.pruneCluster) {
                this.pruneClusterReDraw();
            }
        },
        externalRoutes() {
            if (this.map) {
                this.drawNativePolylines();
            }
        },
        checkedCategories() {
            if (this.map && this.pruneCluster) {
                this.pruneClusterReDraw();
            }
            if (this.map) {
                this.drawNativePolylines();
            }
        },
        checkedExternals() {
            if (this.map) {
                this.drawNativePolylines();
            }
        },
        userLocation() {
            // Set map center to user location if needed
            if (this.userLocation && this.followUser) {
                this.map.setView(this.userLocation);
            }
        },
        popupItem() {
            if (this.popupItem) {
                let viewData = {
                    viewType: 'INFOWINDOW_OPEN'
                };
                if (this.popupItem.name) {
                    // It's external source
                    viewData.externalId = this.popupItem.id;
                    viewData.externalTitle = this.popupItem.title;
                    viewData.externalUrl = this.popupItem.url;
                    viewData.externalApiUrl = this.popupItem.articleApiUrl;
                    viewData.externalSourceName = this.popupItem.name;
                } else {
                    // It's not external source
                    if (this.popupItem.waypoints) {
                        // It has waypoints so it must be a route
                        viewData.routeId = this.popupItem.id;
                    } else {
                        // No waypoints so it must be a place
                        viewData.placeId = this.popupItem.id;
                    }
                }
                eventBus.$emit('infoWindowOpen', viewData);
            }
        }
    },
    data() {
        return {
            map: null,
            pruneCluster: new PruneClusterForLeaflet(),
            polylineGroup: null,
            url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
            errorTileUrl: null,
            attribution: '&copy; <a href="http://osm.org/copyright">OpenStreetMap</a>',
            mapOptions: {
                zoomControl: false,
                zoomSnap: 1,
                minZoom: 1,
                maxZoom: 18,
                maxBoundsViscosity: 1.0,
                preferCanvas: true,
            },
            latLng: latLng,
            showMap: false,
            filters: {
                tags: [],
                badges: []
            },
            markersLastLoadBounds: null,
            routesLastLoadBounds: null,
            tileLayerVisible: true,
            popupItem: null,
            tooltipItem: null
            /*,
            geosearchOptions: {
                provider: new OpenStreetMapProvider(),
                searchLabel: geosearchLabel,
                autoClose: true
            }*/
        };
    },
    created() {
        // Set event bus listeners. Listeners which need the map ref are
        // defined in a separate setListeners function!
        
        eventBus.$on('setFiltersTags', (checkedTags) => {
            this.filters.tags = checkedTags;
        });

        eventBus.$on('pruneClusterReDraw', this.pruneClusterReDraw);

        /**
         * Listens for setBaseLayer event emitted from MapSettings base layer select menu.
         */
        eventBus.$on('setBaseLayer', (baseLayer) => {
            // Get default baseLayer if not set
            if (! baseLayer) {
                baseLayer = this.config.mapLayers[0];
            }
            // Set mapOptions min/max zoom according to base layer min/max zoom
            this.mapOptions.minZoom = baseLayer.minZoom;
            this.mapOptions.maxZoom = baseLayer.maxZoom;
            // Set minZoom based on maxBounds
            if (this.map.options.maxBounds) {
                this.mapOptions.minZoom = this.map.getBoundsZoom(this.map.options.maxBounds);
            }
            // Set zoom to min/max if current zoom is less/greater than min/max
            if (this.map.getZoom() < baseLayer.minZoom) {
                this.map.setZoom(baseLayer.minZoom);
            } else if (this.map.getZoom() > baseLayer.maxZoom) {
                this.map.setZoom(baseLayer.maxZoom);
            }
            // Emit min/max zoom change event to settings panel
            eventBus.$emit('mapMinZoomChanged', this.mapOptions.minZoom);
            eventBus.$emit('mapMaxZoomChanged', this.mapOptions.maxZoom);
            // Change base layer urls
            this.url = baseLayer.url;
            this.errorTileUrl = baseLayer.errorTileUrl;
            // Get Leaflet tile pane
            let tilePane = document.querySelector('.leaflet-tile-pane');
            // Remove all baseLayer classes from tile pane
            if (tilePane) {
                for (let i in this.config.mapLayers) {
                    tilePane.classList.remove(this.config.mapLayers[i].name);
                }
                // Set current baselayer class to tile pane
                tilePane.classList.add(baseLayer.name);
            }
        });
    },
    /*updated() {
        console.log('Map component updated, routes rendered at ' + Date.now());
    },*/
    mounted() {
        // Show map on mount, othwerwise there will be rendering issues
        this.showMap = true;

        // Get ref to map on nextTick and set event bus listeners
        this.$nextTick(() => {
            this.map = this.$refs.map.mapObject;
            setListeners(this, this.map);
            this.setRoutesDisabled();
            eventBus.$emit('loadMarkers', this.map.getBounds());
            eventBus.$emit('loadRoutes', this.map.getBounds());
            this.loadExternalMarkers();
        });
    },
    methods: {
        mapReady() {
            eventBus.$emit('mapZoomChanged', this.map.getZoom());
            eventBus.$emit('mapMinZoomChanged', this.map.getMinZoom());
            eventBus.$emit('mapMaxZoomChanged', this.map.getMaxZoom());
            eventBus.$emit('setBaseLayer', this.config.mapLayers[0]);

            this.pruneClusterReDraw();

            // Override PruneCluster PrepareLeafletMarker method
            this.pruneCluster.PrepareLeafletMarker = (leafletMarker, data) => {
                // Set marker icon
                //leafletMarker.setIcon(this.pruneClusterMarkerIcon(data.id));
                // Set marker icon
                if (data.name) {
                    // External source marker
                    leafletMarker.setIcon(this.pruneClusterExternalMarkerIcon(data.name));
                } else {
                    leafletMarker.setIcon(this.pruneClusterMarkerIcon(data.id));
                }

                // Marker mouseover handler
                /*leafletMarker.on('mouseover touchstart', () => {
                    this.tooltipItem = this.getMarkerById(data.id);
                    // Override tooltipItem location since the marker can be spiderfied
                    this.tooltipItem.latitude = leafletMarker.getLatLng().lat;
                    this.tooltipItem.longitude = leafletMarker.getLatLng().lng;
                });*/

                leafletMarker.on('mouseover touchstart', () => {
                    let pixelCoords = this.map.latLngToContainerPoint(leafletMarker.getLatLng());
                    if (data.name) {
                        // Get external marker by set name and marker id
                        this.tooltipItem = this.getExternalMarker(data.name, data.id);
                    } else {
                        this.tooltipItem = this.getMarkerById(data.id);
                    }
                    this.tooltipItem.x = pixelCoords.x;
                    this.tooltipItem.y = pixelCoords.y;
                });

                leafletMarker.on('mouseout touchend touchcancel', () => {
                    this.tooltipItem = null;
                });

                // Marker click event handler
                leafletMarker.on('click', () => {
                    //console.log('PruneCluster marker click');
                    if (data.name) {
                        // Get external marker by set name and marker id
                        //this.popupItem = this.getExternalMarker(data.name, data.id);
                        this.externalMarkerPopupOpen(data.name, data.id);
                    } else {
                        //this.popupItem = this.getMarkerById(data.id);
                        this.popupOpen(data.id);
                    }
                });
            };

            // Override PruneCluster BuildLeafletCluster method to make it work with Leaflet Glify
            // I guess this is obselete now that Glify isn't used anymore?!?
            /*this.pruneCluster.BuildLeafletCluster = (cluster, position) => {
                var m = new L.Marker(position, {
                    icon: this.pruneCluster.BuildLeafletClusterIcon(cluster)
                });

                m.on('click', () => {
                    // Compute the  cluster bounds (it's slow : O(n))
                    var markersArea = this.pruneCluster.Cluster.FindMarkersInArea(cluster.bounds);
                    var b = this.pruneCluster.Cluster.ComputeBounds(markersArea);

                    if (b) {
                        var bounds = new L.LatLngBounds(
                            new L.LatLng(b.minLat, b.maxLng),
                            new L.LatLng(b.maxLat, b.minLng));

                        var zoomLevelBefore = this.pruneCluster._map.getZoom();
                        var zoomLevelAfter = this.pruneCluster._map.getBoundsZoom(bounds, false, new L.Point(20, 20, null));

                        // If the zoom level doesn't change
                        if (zoomLevelAfter === zoomLevelBefore) {
                            // Send an event for the LeafletSpiderfier
                            this.pruneCluster._map.fire('overlappingmarkers', {
                                cluster: this.pruneCluster,
                                markers: markersArea,
                                center: m.getLatLng(),
                                marker: m
                            });

                            this.pruneCluster._map.setView(position, zoomLevelAfter);
                        } else {
                            this.pruneCluster._map.fitBounds(bounds);
                        }
                    }
                });

                m.on('mouseover touchstart', ()=> {
                    // Do mouseover stuff here
                    //console.log('mouseover cluster');
                    this.tooltipItem = null;
                });

                m.on('mouseout touchend touchcancel', () => {
                    // Do mouseout stuff here
                    this.tooltipItem = null;
                });

                return m;
            };*/

            this.map.addLayer(this.pruneCluster);
        },
        mapClick() {
            if (this.config.fullscreenOnFocus) {
                this.$store.commit('setFullscreen', true);
            }
            eventBus.$emit('refreshMapSize');
            this.popupItem = null;
            this.tooltipItem = null;
        },
        redrawMarkers() {
            // Force refresh markers
            let tmp = this.markers;
            this.$store.commit('setMarkers', []);
            this.$store.commit('setMarkers', tmp);
            tmp = null;
        },
        setRoutesDisabled() {
            if (store.state.config.routesZoomThreshold) {
                let routesDisabled = this.map.getZoom() < store.state.config.routesZoomThreshold;
                this.$store.commit('setRoutesDisabled', routesDisabled);
                if (routesDisabled) {
                    this.clearRoutes();
                }
            }
        },
        zoomUpdate(zoom) {
            eventBus.$emit('mapZoomChanged', zoom);
            this.setRoutesDisabled();
            // Dispatch setInitZoom to dashboard via window.emitter
            if (window.emitter) {
                window.emitter.emit('setInitZoom', zoom);
            }
            this.redrawMarkers();
        },
        centerUpdate(center) {
            this.currentCenter = center;
            // Dispatch setInitLocation to dashboard via window.emitter
            if (window.emitter) {
                window.emitter.emit('setInitLocation', {
                    lat: center.lat,
                    lng: center.lng
                });
            }
        },
        boundsUpdate(bounds) {
            if (this.showMarkers) {
                // Load markers only if new bounds are not inside markersLastLoadBounds
                if (! this.markersLastLoadBounds || ! this.markersLastLoadBounds.contains(bounds)) {
                    this.markersLastLoadBounds = bounds;
                    eventBus.$emit('loadMarkers', this.map.getBounds());
                    this.loadExternalMarkers();
                }
            }
            if (this.showRoutes && ! this.routesDisabled) {
                // Load routes only if new bounds are not inside routesLastLoadBounds
                if (! this.routesLastLoadBounds || ! this.routesLastLoadBounds.contains(bounds)) {
                    this.routesLastLoadBounds = bounds;
                    eventBus.$emit('loadRoutes', this.map.getBounds());
                }
            }
        },
        getOverlayLayerKey(name) {
            if (this.map) {
                return name + '-' + this.map.getZoom();
            }
            return name;
        },
        getOverlayLayerUrl(url) {
            if (Array.isArray(url)) {
                if (! this.map) {
                    // Map not yet initialized
                    return '';
                }
                // Url is an array so there's multiple images for different zoom levels
                for (let i in url) {
                    let item = url[i];
                    if (item.zoom <= this.map.getZoom()) {
                        return item.url;
                    }
                }
            }
            return url;
        },
        pruneClusterReDraw() {
            this.pruneCluster.RemoveMarkers();
            
            // Add normal markers
            this.markers.forEach((marker) => {
                if (this.hasCheckedCategory(marker.categories)) {
                    let pruneClusterMarker = new PruneCluster.Marker(marker.latitude, marker.longitude);
                    pruneClusterMarker.data.id = marker.id;
                    pruneClusterMarker.data.tooltip = marker.title;
                    this.pruneCluster.RegisterMarker(pruneClusterMarker);
                }
            });
            
            // Add external markers
            for (let sourceId in this.externalMarkers) {
                let externalMarkersArr = this.externalMarkers[sourceId];
                externalMarkersArr.forEach((marker) => {
                    let pruneClusterMarker = new PruneCluster.Marker(marker.latitude, marker.longitude);
                    pruneClusterMarker.data.id = marker.id;
                    pruneClusterMarker.data.tooltip = marker.title;
                    pruneClusterMarker.data.name = marker.name;
                    this.pruneCluster.RegisterMarker(pruneClusterMarker);
                });
            }
            
            this.pruneCluster.ProcessView();
        },
        drawNativePolylines() {
            //console.log('draw routes');
            //console.log('Starting to draw native polyline routes: ' + timeElapsed());
            this.clearRoutes();

            this.polylineGroup = L.layerGroup();

            for (let i in this.routes) {
                // Skip routes with no checked categories
                if (! this.hasCheckedCategory(this.routes[i].categories)) {
                    continue;
                }

                this.drawPolyline(this.routes[i], i);
            }

            //console.log(this.externalRoutes);

            for (let prop in this.externalRoutes) {
                for (let i in this.externalRoutes[prop]) {
                    // Draw route only if external set is checked
                    if (this.checkedExternals.includes(prop)) {
                        this.drawPolyline(this.externalRoutes[prop][i], i);
                    }
                }
            }

            this.polylineGroup.addTo(this.map);
            //console.log('Native polyline routes drawn: ' + timeElapsed());
        },
        drawPolyline(route, i) {

            if (! route) {
                console.error('Route undefined');
                return;
            }

            let waypointsArr = route.waypoints;
            
            for (let j in waypointsArr) {
                let waypoints = waypointsArr[j];
                
                // Set polyline options to local varibles to be used in polyline events
                let strokeColor = this.config.polylineOptions.strokeColor;
                let strokeHoverColor = this.config.polylineOptions.strokeHoverColor;
                let strokeWeight = this.config.polylineOptions.strokeWeight;

                // If the route has color define use it instead of default color
                if (route.color) {
                    strokeColor = route.color;
                }

                let polylineOptions = {
                    color: strokeColor,
                    weight: strokeWeight
                };
                
                let polyline = L.polyline(waypoints, polylineOptions).addTo(this.map);

                polyline.on('click', (e) => {
                    this.popupItem = route;
                    // Set popupItem latitude and longitude
                    this.popupItem.latitude = e.latlng.lat;
                    this.popupItem.longitude = e.latlng.lng;
                    this.routePopupOpen(i, e.latlng, route.name);
                });

                // Change polyline color and bring it to front on hover
                polyline.on('mouseover touchstart', function() {
                    this.setStyle({
                        color: strokeHoverColor
                    });
                    this.bringToFront();
                });

                // Restore polyline color on mouseout
                polyline.on('mouseout touchend touchcancel', function() {
                    this.setStyle({
                        color: strokeColor
                    });
                });

                this.polylineGroup.addLayer(polyline);
            }
        },
        clearRoutes() {
            if (this.polylineGroup) {
                this.polylineGroup.clearLayers();
                this.polylineGroup.removeFrom(this.map);
                this.polylineGroup = null;
            }
        },
        popupReady(e) {
            e._source.openPopup();
        },
        popupClose() {
            this.popupItem = null;
        },
        /**
         * @param   {number}    markerId
         */
        popupOpen(markerId) {
            if (this.popupItem) {
                // Antoher popup was open, set to null and open this with a slight delay
                this.popupItem = null;
                window.setTimeout(() => {
                    this.popupItem = this.getMarkerById(markerId);
                }, 500);
            } else {
                this.popupItem = this.getMarkerById(markerId);
            }
        },
        /**
         * @param   {string}    setName
         * @param   {number}    markerId
         */
        externalMarkerPopupOpen(setName, markerId) {
            if (this.popupItem) {
                // Antoher popup was open, set to null and open this with a slight delay
                this.popupItem = null;
                window.setTimeout(() => {
                    this.popupItem = this.getExternalMarker(setName, markerId);
                }, 500);
            } else {
                this.popupItem = this.getExternalMarker(setName, markerId);
            }
        },
        routePopupOpen(index, latlng, external) {
            let popupItem = {};

            if (! external) {
                popupItem = this.routes[index];
            } else {
                popupItem = this.externalRoutes[external][index];
            }
            
            popupItem.latitude = latlng.lat;
            popupItem.longitude = latlng.lng;
            
            if (this.popupItem) {
                this.popupItem = null;
                window.setTimeout(() => {
                    this.popupItem = popupItem;
                }, 500);
            } else {
                this.popupItem = popupItem;
            }
        },
        pruneClusterMarkerIcon(markerId) {
            let iconUrl = this.getMarkerIconById(markerId);

            if (this.themeColor && this.themeTextColor) {
                // Theme colors are set, get icon url via global method to set the color params
                iconUrl = this.$setIconUrl(iconUrl, this.themeColor, this.themeTextColor);
            }

            return L.icon({
                iconUrl: iconUrl,
                iconSize: [32, 32],
                iconAnchor: [16, 16],
                tooltipAnchor: [16, 0],
                popupAnchor: [0, -16]
            });
        },
        pruneClusterExternalMarkerIcon(sourceId) {
            let source = this.getExternalSourceById(sourceId);

            return L.icon({
                iconUrl: source.icon,
                iconSize: [32, 32],
                iconAnchor: [16, 16],
                tooltipAnchor: [16, 0],
                popupAnchor: [0, -16]
            });
        },
        getExternalSourceById(id) {
            let source;
            for (let sourceSet in this.config.externalSources) {
                let sources = this.config.externalSources[sourceSet].sources;
                let match = sources.find(item => item.id == id);
                if (match) {
                    source = match;
                    break;
                }
            }
            return source;
        },
        isSVG(item) {
            let fileName = this.getItemIcon(item);
            return fileName.split('.').pop() === 'svg';
        },
        getMarkerById(markerId) {
            return this.markers.find(item => item.id == markerId);
        },
        getExternalMarker(setName, markerId) {
            return this.externalMarkers[setName].find(item => item.id == markerId);
        },
        getMarkerIconById(markerId) {
            let marker = this.markers.find(item => item.id == markerId);
            if (marker) {
                return this.getItemIcon(marker);
            }
            return null;
        },
        getItemIcon(item) {
            for (let i = 0; i < item.categories.length; i++) {
                if (this.checkedCategories.includes(item.categories[i].id)) {
                    return item.categories[i].icon;
                }
            }
            return false;
        },
        getMarkerKey(id) {
            return 'marker-' + id;
        },
        getRouteKey(id) {
            return 'route-' + id;
        },
        getRouteMarkerKey(id) {
            return 'route-marker-' + id;
        },
        getRouteSegmentMarkerKey(id) {
            return 'route-segment-marker-' + id;
        },
        getExternalMarkerKey(id) {
            return 'external-marker-' + id;
        },
        getExternalRouteKey(id) {
            return 'external-route-' + id;
        },
        getExternalIconSize(markerSet) {
            return [markerSet.iconW,markerSet.iconH];
        },
        /*getRouteLength(waypoints) {
            let distance = 0;
            let prevPoint = null;
            waypoints.forEach(function(waypoint) {
                let latLng = L.latLng([waypoint.lat, waypoint.lng]);
                if (prevPoint) {
                    distance += prevPoint.distanceTo(latLng);
                }
                prevPoint = latLng;
            });
            return (distance / 1000).toFixed(2);
        },*/
        getStrokeColor(route) {
            if (route.polylineOptions) {
                return route.polylineOptions.strokeColor;
            }
            return this.config.polylineOptions.strokeColor;
        },
        getStrokeWeight(route) {
            if (route.polylineOptions) {
                return route.polylineOptions.strokeWeight;
            }
            return this.config.polylineOptions.strokeWeight;
        },
        getArticleApiUrl(item) {
            if (item.articleApiUrl) {
                return item.articleApiUrl.replace('{id}', item.id);
            }
            // No articleApiUrl defined, return default
            if (item.waypoints) {
                return this.$store.state.config.routesBaseUrl + '/' + item.id;
            } else {
                return this.$store.state.config.markersBaseUrl + '/' + item.id;
            }
        },
        hasCheckedCategory(categories) {
            if (! categories) {
                return false;
            }
            for (let i = 0; i < categories.length; i++) {
                if (this.checkedCategories.includes(categories[i].id)) {
                    return true;
                }
            }
            return false;
        },
        hasCheckedTag(tags) {
            if (! tags) {
                return false;
            }
            for (let i = 0; i < tags.length; i++) {
                if (this.filters.tags.includes(tags[i].id)) {
                    return true;
                }
            }
            return false;
        },
        waypointsToLatLngs(waypoints) {
            let latLngs = [];
            for (let i = 0; i < waypoints.length; i++) {
                latLngs.push([waypoints[i].lat, waypoints[i].lng])
            }
            return latLngs;
        },
        loadExternalMarkers() {
            for (let i in this.checkedExternals) {
                eventBus.$emit('loadExternals', this.checkedExternals[i], this.map.getBounds());
            }
        },
        pDistance(x, y, x1, y1, x2, y2) {
            const A = x - x1;
            const B = y - y1;
            const C = x2 - x1;
            const D = y2 - y1;
        
            const dot = A * C + B * D;
            const len_sq = C * C + D * D;
            let param = -1;

            if (len_sq !== 0) {
                // in case of 0 length line
                param = dot / len_sq;
            }

            let xx, yy;
        
            if (param < 0) {
                xx = x1;
                yy = y1;
            } else if (param > 1) {
                xx = x2;
                yy = y2;
            } else {
                xx = x1 + param * C;
                yy = y1 + param * D;
            }
        
            let dx = x - xx;
            let dy = y - yy;
            return Math.sqrt(dx * dx + dy * dy);
        },
        /*routesToGeoJSON(routes) {
            //console.log('Starting to parse route data to GeoJSON: ' + timeElapsed());
            let geoJSON = {
                "type": "FeatureCollection",
                "features": []
            };

            for (let i in routes) {
                // Skip routes with no checked categories
                if (! this.hasCheckedCategory(routes[i].categories)) {
                    continue;
                }

                let waypointsArr = routes[i].waypoints;
                
                for (let j in waypointsArr) {

                    let waypoints = waypointsArr[j];
                    let coordinates = [];

                    waypoints.forEach(function(coords) {
                        coordinates.push([coords.lat, coords.lng]);
                    });

                    // Skip routes with no coordinates
                    if (coordinates.length == 0) {
                        continue;
                    }

                    geoJSON.features.push({
                        "type": "Feature",
                        "geometry": {
                            "type": "LineString",
                            "coordinates": coordinates
                        },
                        "properties": {
                            "id": routes[i].id,
                            "title": routes[i].title,
                            "image": routes[i].image,
                            "description": routes[i].description,
                            "url": routes[i].url,
                            "hasText": routes[i].hasText,
                            "hasGpx": routes[i].hasGpx
                        }
                    });
                }
            }
            
            //console.log('Routes parsed to GeoJSON: ' + timeElapsed());
            return geoJSON;
        }*/
    }
};
</script>

<style scoped>

</style>
